import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { interpretationsApi } from '../../services/api';

const REOPEN_REASONS = [
  'Aprobé sin querer',
  'Falta agregar descripción',
  'Datos del paciente incorrectos',
  'Error en la interpretación',
  'Otro (ver descripción)',
] as const;

interface ReopenModalProps {
  show: boolean;
  interpretationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const ReopenModal: React.FC<ReopenModalProps> = ({
  show,
  interpretationId,
  onSuccess,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isOtro = reason === 'Otro (ver descripción)';
  const descriptionRequired = isOtro;

  function reset() {
    setReason('');
    setDescription('');
    setTotpCode('');
    setTotpError('');
    setSubmitting(false);
    setError('');
  }

  function handleCancel() {
    reset();
    onCancel();
  }

  function handleTotpBlur() {
    if (totpCode && !/^[0-9]{6}$/.test(totpCode)) {
      setTotpError('El código debe ser exactamente 6 dígitos.');
    } else {
      setTotpError('');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!reason) {
      setError('Selecciona un motivo.');
      return;
    }
    if (descriptionRequired && !description.trim()) {
      setError('La descripción es obligatoria para "Otro (ver descripción)".');
      return;
    }
    if (!/^[0-9]{6}$/.test(totpCode)) {
      setTotpError('El código debe ser exactamente 6 dígitos.');
      return;
    }

    setSubmitting(true);
    try {
      await interpretationsApi.reopen(interpretationId, {
        reason,
        description: description.trim() || undefined,
        totp_code: totpCode,
      });
      reset();
      onSuccess();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Código TOTP inválido.');
      } else if (status === 409) {
        setError('Esta interpretación no puede reabrirse en su estado actual.');
      } else {
        setError(err?.response?.data?.error || err?.message || 'Error al reabrir la interpretación.');
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal show={show} onHide={handleCancel} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Reabrir interpretación</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-3" controlId="reopen-reason">
            <Form.Label>
              Motivo <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Seleccionar motivo...</option>
              {REOPEN_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="reopen-description">
            <Form.Label>
              Descripción{descriptionRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={250}
              required={descriptionRequired}
              placeholder={descriptionRequired ? 'Describe el motivo...' : 'Descripción adicional (opcional)'}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="reopen-totp">
            <Form.Label>
              Código TOTP <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              onBlur={handleTotpBlur}
              placeholder="000000"
              isInvalid={!!totpError}
            />
            {totpError && (
              <Form.Control.Feedback type="invalid">{totpError}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="warning" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Reabriendo...
              </>
            ) : (
              'Reabrir'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReopenModal;
